<template>
    <div class="sc-page">
        <div class="sc-wrapper">
            <!-- Header -->
            <div class="sc-header">
                <div class="sc-logo-wrap">
                    <v-img
                        v-if="company_logo"
                        :src="api_url + company_logo"
                        contain
                        max-height="72"
                        max-width="220"
                        class="sc-logo"
                    />
                    <div v-else class="sc-logo-placeholder">GoGroupBooking</div>
                </div>
                <h1 class="sc-title">Payment Confirmation</h1>
                <p class="sc-subtitle">Please review the payments below and confirm or decline each one.</p>
            </div>

            <!-- Table card -->
            <div class="sc-card">
                <v-simple-table class="sc-table">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Service</th>
                                <th>Date/Time</th>
                                <th>Details</th>
                                <th>Notes</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="build in builds" :key="build.id" class="sc-row">
                                <td class="sc-service">{{ build.option ? build.option.name : '—' }}</td>
                                <td>
                                    <div v-if="build.start_date">{{ $date(build.start_date).format('DD/MM/YYYY') }}</div>
                                    <div v-if="build.start_time" style="color:#3988AA;">{{ isAccom(build) ? 'Check-in: ' : '' }}{{ build.start_time }}</div>
                                    <span v-if="!build.start_date && !build.start_time">—</span>
                                </td>
                                <td>
                                    <div v-if="isAccom(build)">
                                        <div v-for="(item, i) in getAccomDetails(build)" :key="i" :class="{ 'mt-2': i > 0 }">
                                            <strong>Day {{ i + 1 }}:</strong><br>
                                            &nbsp;&nbsp;<strong>Rate:</strong> {{ currency }}{{ item.rate }}<br>
                                            &nbsp;&nbsp;<strong>Groupsize:</strong> {{ item.groupsize }} pax
                                        </div>
                                    </div>
                                    <div v-else>
                                        <div v-if="getActivityRate(build)"><strong>Rate:</strong> {{ currency }}{{ getActivityRate(build) }}{{ rateSuffix(build) }}</div>
                                        <div><strong>Groupsize:</strong> {{ build.groupsize || '—' }} pax</div>
                                    </div>
                                </td>
                                <td style="white-space: pre-line;">{{ build.booking_notes || '—' }}</td>
                                <td>
                                    <span v-if="build.payment_status_id === 3" class="sc-badge sc-badge--confirmed">
                                        <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>Confirmed
                                    </span>
                                    <span v-else-if="build.payment_status_id === 4" class="sc-badge sc-badge--declined">
                                        <v-icon small class="mr-1">mdi-close-circle-outline</v-icon>Declined
                                    </span>
                                    <div v-else class="sc-actions">
                                        <v-btn
                                            depressed
                                            small
                                            color="#E8F5E9"
                                            class="sc-btn-confirm mr-2"
                                            :loading="build.loading"
                                            @click="confirmPayment(build)"
                                        >
                                            <v-icon small left>mdi-check</v-icon>Confirm
                                        </v-btn>
                                        <v-btn
                                            depressed
                                            small
                                            color="#FFEBEE"
                                            class="sc-btn-decline"
                                            :loading="build.loading"
                                            @click="declinePayment(build)"
                                        >
                                            <v-icon small left>mdi-close</v-icon>Decline
                                        </v-btn>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </div>

            <p class="sc-footer">If you have any questions, please contact us directly.</p>
        </div>
    </div>
</template>

<script>
import { rateSuffix } from "@/utils/finalRates";

export default {
    components: {},
    props: [],
    data: () => ({
        builds: [],
        currency: '€',
        company_logo: null,
        api_url: process.env.VUE_APP_API_URL || '',
    }),
    async mounted() {
        await this.getBuilds();
    },
    methods: {
        rateSuffix,
        async getBuilds() {
            const { event_id, supplier_id, build_ids } = this.$route.params;
            await this.$axios.get(`api/supplier/builds/${event_id}/${supplier_id}/${build_ids}`)
                .then(({ data }) => {
                    if (data.response) {
                        this.builds = data.data.map(b => ({ ...b, loading: false }));
                        this.currency = data.currency || '€';
                        this.company_logo = data.company_logo || null;
                    }
                });
        },
        async confirmPayment(build) {
            build.loading = true;
            const { event_id, supplier_id } = this.$route.params;
            await this.$axios.post(`api/supplier/confirm_payment_build/${event_id}/${supplier_id}/${build.id}`)
                .then(({ data }) => {
                    if (data.response) {
                        build.payment_status_id = 3;
                        this.$toast.success(data.message);
                    }
                })
                .finally(() => { build.loading = false; });
        },
        async declinePayment(build) {
            build.loading = true;
            const { event_id, supplier_id } = this.$route.params;
            await this.$axios.post(`api/supplier/decline_payment_build/${event_id}/${supplier_id}/${build.id}`)
                .then(({ data }) => {
                    if (data.response) {
                        build.payment_status_id = 4;
                        this.$toast.success(data.message);
                    }
                })
                .finally(() => { build.loading = false; });
        },
        isAccom(build) {
            if (build.accom_groupsize) {
                try {
                    const parsed = Array.isArray(build.accom_groupsize)
                        ? build.accom_groupsize
                        : JSON.parse(build.accom_groupsize)
                    return Array.isArray(parsed) && parsed.length > 0
                } catch (e) { /* invalid JSON */ }
            }
            return false
        },
        getAccomDetails(build) {
            let groupsizes = []
            let rates = []
            if (build.accom_groupsize) {
                try {
                    groupsizes = Array.isArray(build.accom_groupsize)
                        ? build.accom_groupsize
                        : JSON.parse(build.accom_groupsize)
                } catch (e) { /* invalid JSON */ }
            }
            if (build.accom_final_rates) {
                try {
                    rates = Array.isArray(build.accom_final_rates)
                        ? build.accom_final_rates
                        : JSON.parse(build.accom_final_rates)
                } catch (e) { /* invalid JSON */ }
            }
            return groupsizes.map((size, i) => ({
                groupsize: size,
                rate: rates[i] !== undefined ? rates[i] : (build.rate || 0)
            }))
        },
        getActivityRate(build) {
            const rate = build.final_rate !== null && build.final_rate !== undefined ? build.final_rate : build.rate
            return rate > 0 ? rate : null
        },
    },
    watch: {},
};
</script>

<style scoped lang="scss">
.sc-page {
    min-height: 100vh;
    background-color: #F4F6F9;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 60px 16px 80px;
}

.sc-wrapper {
    width: 100%;
    max-width: 860px;
}

.sc-header {
    text-align: center;
    margin-bottom: 32px;
}

.sc-logo-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
}

.sc-logo {
    display: block;
}

.sc-logo-placeholder {
    font-size: 24px;
    font-weight: 700;
    color: #3988AA;
    letter-spacing: -0.5px;
}

.sc-title {
    font-family: 'Manrope', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #101010;
    margin-bottom: 8px;
}

.sc-subtitle {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    color: #7C7C7C;
    margin: 0;
}

.sc-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #E9E9E9;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.sc-table {
    th {
        font-family: 'Manrope', sans-serif;
        font-size: 11px;
        font-weight: 700;
        color: #7C7C7C;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        background-color: #F7F9FB;
        border-bottom: 1px solid #E9E9E9 !important;
        padding: 14px 16px !important;
    }

    td {
        font-family: 'Manrope', sans-serif;
        font-size: 13px;
        color: #333333;
        padding: 14px 16px !important;
        border-bottom: 1px solid #F0F0F0;
    }
}

.sc-row:last-child td {
    border-bottom: none;
}

.sc-service {
    font-weight: 600;
    color: #101010;
}

.sc-badge {
    display: inline-flex;
    align-items: center;
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;

    &--confirmed {
        background-color: #E8F5E9;
        color: #2E7D32;
    }

    &--declined {
        background-color: #FFEBEE;
        color: #C62828;
    }
}

.sc-actions {
    display: flex;
    align-items: center;
}

.sc-btn-confirm {
    color: #2E7D32 !important;
    font-weight: 600 !important;
    font-family: 'Manrope', sans-serif !important;
    font-size: 12px !important;
    letter-spacing: 0.2px !important;
    text-transform: none !important;
}

.sc-btn-decline {
    color: #C62828 !important;
    font-weight: 600 !important;
    font-family: 'Manrope', sans-serif !important;
    font-size: 12px !important;
    letter-spacing: 0.2px !important;
    text-transform: none !important;
}

.sc-footer {
    text-align: center;
    margin-top: 24px;
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    color: #AAAAAA;
}
</style>

